import {useState} from "react";
import {Switch} from "@/components/ui/switch.tsx";
import type {Door} from "@/dungeons/model/DungeonGeometry.ts";
import {useMapPopoverLayout} from "@/tabletop/useMapPopoverLayout.ts";
import {useDoorStates} from "@/encounter/doors/DoorStateHooks.ts";

export default function DungeonDoorPopover({door, anchor, viewport}: {
    door: Door;
    anchor: {x: number; y: number};
    viewport: {width: number; height: number};
}) {
    const {stateFor, setDoorState} = useDoorStates();
    const [busy, setBusy] = useState(false);
    const {panelRef, contentRef, style} = useMapPopoverLayout({anchor, viewport, width: 220});
    const isOpen = stateFor(door) === "OPEN";
    const toggle = async (checked: boolean) => {
        setBusy(true);
        try {
            await setDoorState(door, checked ? "OPEN" : "CLOSED");
        } catch {
            // The HTTP client presents this mutation error as a toast.
        } finally {
            setBusy(false);
        }
    };

    return <div className="pointer-events-none absolute inset-0 z-10">
        <div ref={panelRef} role="dialog" aria-label="Tür"
             className="pointer-events-auto absolute overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-xl"
             style={style}>
            <div ref={contentRef} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="min-w-0 wrap-break-word text-sm font-semibold">{door.label?.trim() || "Tür"}</h3>
                    <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs">
                        {isOpen ? "Geöffnet" : "Geschlossen"}
                        <Switch checked={isOpen} disabled={busy} onCheckedChange={checked => void toggle(checked)}/>
                    </label>
                </div>
                {door.notes && <p className="whitespace-pre-wrap text-sm leading-relaxed">{door.notes}</p>}
            </div>
        </div>
    </div>;
}
